import NavBar from "frontend/Components/DesignSystem/NavBar";
import HeroAnimation from "frontend/Components/HeroAnimation/HeroAnimation";
import React from "react";
import "../../../../css/Customer/Pages/OurServicesPage/Service.css";
import FAQContainer from "frontend/Components/DesignSystem/FAQ/FAQContainer";
import CardProduct from "frontend/Components/DesignSystem/Cards/CardProduct";
import { ReactComponent as MortagePlanningIcon } from "../../../../assets/icons/Mortgage-Planning.svg";
import { ReactComponent as CorporatePlanningIcon } from "../../../../assets/icons/Corporate-Planning.svg";
import { ReactComponent as InvestmentPlanningIcon } from "../../../../assets/icons/Investment-Planning.svg";
import { ReactComponent as EquityReleaseIcon } from "../../../../assets/icons/Equity -Release.svg";
import { ReactComponent as InsuranceProtectionIcon } from "../../../../assets/icons/Insurance-Protection.svg";
import { ReactComponent as RetirementPlanningIcon } from "../../../../assets/icons/Retirement-Planning.svg";
import AdviserValues from "frontend/Components/AdviserValues/AdviserValue";
import { ReactComponent as NumberOneIcon } from "../../../../assets/icons/Number 1.svg";
import { ReactComponent as NumberTwoIcon } from "../../../../assets/icons/Number 2.svg";
import { ReactComponent as NumberThreeIcon } from "../../../../assets/icons/Number 3.svg";
import { ReactComponent as NumberFourIcon } from "../../../../assets/icons/Number 4.svg";
import Testimonials from "../../../Components/Testimonials/Testimonials";
import { ContactUs } from "frontend/Components/ContactForm/ContactUs";
import Footer from "frontend/Components/DesignSystem/Footer";
import SeoHelmet from "frontend/Components/SEO/SeoHelmet";
import { SEO_PAGES, getPageUrl } from "frontend/Utils/seoConfig";
import ImageDirectory from "frontend/HelperFunctions/ImageDirectory";

/**
 * InsuranceProtection component renders the Insurance/Protection Advice page.
 *
 * This page provides information about different types of insurance and protection products,
 * answers frequently asked questions, and highlights the company's adviser values and services.
 * It includes navigation, hero section, FAQ, service cards, adviser values, testimonials, contact form, and footer.
 *
 * @component
 * @returns {JSX.Element} The rendered Insurance/Protection Advice page.
 */
const InsuranceProtection = () => {
  const navigationItems = [
    { label: "Home", navigateTo: "/" },
    { label: "Our Services", navigateTo: "/OurServices" },
    { label: "About Us", navigateTo: "/AboutUs" },
    { label: "Contact Us", navigateTo: "/ContactUs" },
  ];

  const faqs = [
    {
      question: "What insurance do I need?",
      answer:
        "There are several different insurances, including life cover, critical illness cover and income protection.   The options will be discussed alongside whatever your most pressing need areas are.",
    },

    {
      question: "Do I need protection?",
      answer:
        "Generally speaking, most people need some sort of life cover protection.  The best example is for the parents of a family with young children.  This would be to help ensure the children don’t suffer too much financially, following the death of a parent.  However there can be other needs later in life, such as to cover a mortgage or some other type of liability.   ",
    },
    {
      question: "What is protection?",
      answer:
        "The example above is protection to pay out on death, to help your more vulnerable family members. However, there are also income protection and critical illness policies.     These are designed to pay out  to you, in the event of you being too ill to work for example.  ",
    },
    {
      question: "When should I review my protection policy?",
      answer:
        "You may find that an existing policy is no longer sufficient.   For example you may now have more children or a larger mortgage.   Alternatively, there may now be more competitive providers available.  ",
    },
    {
      question: "How much will this cost?",
      answer:
        "The cost of a policy varies based on the amount you want to be insured for, your age and your health.  Our advisers will help you by searching the whole market to find the most competitively priced provider for you.  ",
    },
  ];
  const values = [
    {
      icon: <NumberOneIcon />,
      text: "You are our most important person",
    },
    {
      icon: <NumberTwoIcon />,
      text: "Recommendations are planned out and delivered to you",
    },
    {
      icon: <NumberThreeIcon />,
      text: "Deliver the best outcomes for you",
    },
    {
      icon: <NumberFourIcon />,
      text: "We talk you through every step of the process",
    },
  ];

  return (
    <div className="Service">
      <SeoHelmet
        page={SEO_PAGES.insuranceProtection}
        service={{
          name: "Insurance and Protection Advice",
          description:
            "Rosemount Financial Solutions provides insurance and protection advice, helping clients review life cover, critical illness cover, income protection, mortgage protection and family financial protection needs.",
          serviceType: [
            "Insurance Advice",
            "Protection Advice",
            "Life Cover",
            "Critical Illness Cover",
            "Income Protection",
            "Mortgage Protection",
            "Family Protection",
            "Financial Protection Advice",
          ],
        }}
        breadcrumbs={[
          {
            name: "Home",
            url: getPageUrl("/"),
          },
          {
            name: "Our Services",
            url: getPageUrl("/OurServices"),
          },
          {
            name: "Insurance/Protection Advice",
            url: getPageUrl("/OurServices/InsuranceProtection"),
          },
        ]}
        faqs={faqs}
      />
      <NavBar navigationItems={navigationItems} />
      <HeroAnimation
        imgSrc={ImageDirectory["customer-insurance-min"]}
        altText="Papers with a pair of glasses and a pen lay on top."
        children={
          <>
            <h1>Insurance/</h1>
            <h1>Protection Advice</h1>
            <div className="SubTitle">
              Helping you find the right cover for your needs
            </div>
          </>
        }
      />
      <div className="SubTitle-Container">
        <div className="SubTitle">
          Ensuring your family's financial protection is critically important.
          Our advisers will discuss your protection needs and design a solution
          to provide financial support should the worst happen. This could be
          life insurance and critical illness to cover a mortgage debt, income
          protection to protect your income should you be unable to work, or
          maybe a benefit to provide income upon death, to ensure your family
          can continue their lifestyle even if you are no longer around.
        </div>
        <div className="SubTitle">
          Our advisers will ensure that you receive regular reviews regarding
          your protection plans, ensuring the cover you have in place remains
          suitable for your ongoing needs.
        </div>
      </div>
      <FAQContainer faqs={faqs} />
      <div className="Customer-Services-Container">
        <h2>Our Services</h2>
        <div className="Customer-Services">
          <CardProduct
            Icon={MortagePlanningIcon}
            title="Mortgage Planning"
            link="/OurServices/MortgagePlanning"
          />
          <CardProduct
            Icon={CorporatePlanningIcon}
            title="Corporate Planning"
            link="/OurServices/CorporatePlanning"
          />
          <CardProduct
            Icon={InvestmentPlanningIcon}
            title="Investment Planning"
            link="/OurServices/InvestmentPlanning"
          />
          <CardProduct
            Icon={RetirementPlanningIcon}
            title="Retirement Planning"
            link="/OurServices/RetirementPlanning"
          />
          <CardProduct
            Icon={EquityReleaseIcon}
            title="Equity Release"
            link="/OurServices/EquityRelease"
          />
        </div>
      </div>
      <AdviserValues title="Our Adviser Values" values={values} />
      <Testimonials />
      <ContactUs />
      <Footer />
    </div>
  );
};

export default InsuranceProtection;
